import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import supabase from "../../../utils/supabase";
import { toast } from "react-toastify";
import useUserStore from "../../../stores/user";
import { updateRetakeOption } from "../../../utils/retakes";
import ConfirmDeleteAssignmentModal from "../../modals/ConfirmDeleteAssignmentModal";
import EditAssignmentModal from "../../modals/EditAssignmentModal";
import { getStandardTerminology } from "../../../utils/standardsUtils";
import dayjs from "dayjs";
import { useT, useUiLang } from "../../../stores/uiLang";
import { TD_T } from "../../../i18n/teacherDashboard";

interface AssignmentTableProps {
  activeFilter: string;
  searchQuery: string;
}

export default function AssignmentTable({
  activeFilter,
  searchQuery,
}: AssignmentTableProps) {
  const t = useT(TD_T);
  const locale = useUiLang() === "es" ? "es-US" : "en-US";
  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" });
  const fmtTime = (d: string) =>
    new Date(d).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
  const [assignments, setAssignments] = useState<any[]>([]);
  const [completionMap, setCompletionMap] = useState<
    Record<string, { total: number; completed: number }>
  >({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [updatingRetake, setUpdatingRetake] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [assignmentToEdit, setAssignmentToEdit] = useState<any>(null);
  const [sortBy, setSortBy] = useState("due_date");
  const user = useUserStore((state) => state.user);
  const stateName = useUserStore((state) => state.stateName);
  const terminology = getStandardTerminology(stateName || "Oklahoma");

  const itemsPerPage = 5;

  const setCorrectAssignmentType = (type: string) => {
    if (type === "Quizzes") {
      return "quiz";
    } else if (type === "Midterms") {
      return "midterm";
    } else if (type === "Finals") {
      return "final";
    } else if (type === "Projects") {
      return "project";
    }
  };

  async function getAssignments(page = 1) {
    if (!user.id) return;

    setLoading(true);

    let query = supabase
      .from("assignments")
      .select("*", { count: "exact" })
      .eq("user_id", user.id);

    switch (sortBy) {
      case "start_date":
        query = query.order("start_date", { ascending: true });
        break;
      case "created_date":
        query = query.order("created_at", { ascending: false });
        break;
      case "due_date":
        query = query.order("due_date", { ascending: true });
        break;
      case "standard":
        query = query.order("standard", { ascending: true });
        break;
      default:
        query = query.order("due_date", { ascending: true });
    }

    if (activeFilter !== "All") {
      const filterType = setCorrectAssignmentType(activeFilter);
      query = query.eq("type", filterType);
    }

    if (searchQuery.trim()) {
      query = query.or(`assessment_name.ilike.%${searchQuery}%`);
    }

    const from = (page - 1) * itemsPerPage;
    const to = from + itemsPerPage - 1;
    query = query.range(from, to);

    const { data: assignments, error: assignmentsError, count } = await query;

    if (assignmentsError) {
      toast.error("Failed to fetch assignments");
      setLoading(false);
      return;
    }

    setAssignments(assignments || []);
    setTotalCount(count || 0);
    setLoading(false);
    return assignments;
  }

  async function getCompletionMap(assignmentIds: string[]) {
    if (!assignmentIds.length) return;
    const { data: studentAssignments, error } = await supabase
      .from("student_assignments")
      .select("assignment_id, status")
      .in("assignment_id", assignmentIds);
    if (error) {
      toast.error("Failed to fetch student assignment completions");
      return;
    }

    const map: Record<string, { total: number; completed: number }> = {};
    studentAssignments.forEach((sa: any) => {
      if (!map[sa.assignment_id]) {
        map[sa.assignment_id] = { total: 0, completed: 0 };
      }
      map[sa.assignment_id].total += 1;
      if (sa.status === "completed") {
        map[sa.assignment_id].completed += 1;
      }
    });
    setCompletionMap(map);
  }

  async function handleUpdateRetakeOption(assignmentId, newRetakeOption) {
    if (!assignmentId || !newRetakeOption) return;

    setUpdatingRetake(true);
    try {
      const success = await updateRetakeOption(assignmentId, newRetakeOption);

      if (success) {
        setAssignments((prev) =>
          prev.map((assignment) =>
            assignment.id === assignmentId
              ? { ...assignment, retake_option: newRetakeOption }
              : assignment
          )
        );
      }
    } finally {
      setUpdatingRetake(false);
    }
  }

  const handleRetakeChange = (assignmentId: string, newValue: string) => {
    handleUpdateRetakeOption(assignmentId, newValue);
  };

  const handleDeleteAssignment = (assignment: any) => {
    setAssignmentToDelete(assignment);
    setIsDeleteModalOpen(true);
  };

  const handleEditAssignment = (assignment: any) => {
    setAssignmentToEdit(assignment);
    setIsEditModalOpen(true);
  };

  const handleAssignmentDeleted = () => {
    getAssignments(currentPage).then((assignments) => {
      if (assignments && assignments.length > 0) {
        const assignmentIds = assignments.map((a) => a.id);
        getCompletionMap(assignmentIds);
      }
    });
  };

  const handleAssignmentUpdated = () => {
    getAssignments(currentPage).then((assignments) => {
      if (assignments && assignments.length > 0) {
        const assignmentIds = assignments.map((a) => a.id);
        getCompletionMap(assignmentIds);
      }
    });
  };

  useEffect(() => {
    setCurrentPage(1);
    getAssignments(1).then((assignments) => {
      if (assignments && assignments.length > 0) {
        const assignmentIds = assignments.map((a) => a.id);
        getCompletionMap(assignmentIds);
      }
    });
  }, [user?.id, activeFilter, searchQuery, sortBy]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  function getRouteBase(type: string) {
    switch (type) {
      case "midterm":
        return "midterm";
      case "final":
        return "finals";
      case "project":
        return "project";
      default:
        return "quiz";
    }
  }

  const handlePrevious = async () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      const assignments = await getAssignments(newPage);
      if (assignments && assignments.length > 0) {
        const assignmentIds = assignments.map((a) => a.id);
        getCompletionMap(assignmentIds);
      }
    }
  };

  const handleNext = async () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      const assignments = await getAssignments(newPage);
      if (assignments && assignments.length > 0) {
        const assignmentIds = assignments.map((a) => a.id);
        getCompletionMap(assignmentIds);
      }
    }
  };

  const typeColors = {
    quiz: "bg-blue-100 text-blue-800",
    midterm: "bg-purple-100 text-purple-800",
    project: "bg-yellow-100 text-yellow-800",
    final: "bg-green-100 text-green-800",
  };

  return (
    <div className="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg mb-6">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex justify-between items-center">
          <h3 className="text-base font-medium text-gray-900">
            {t.activeAssignments}
          </h3>
          <div className="flex items-center space-x-2 relative">
            <label
              htmlFor="sort-select"
              className="text-sm font-medium text-gray-700"
            >
              {t.sortBy}
            </label>
            <select
              id="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-md border border-gray-300 shadow-sm pl-3 pr-10 py-2 text-sm bg-white focus:ring-indigo-500 focus:border-indigo-500 appearance-none"
            >
              <option value="due_date">{t.dueDate}</option>
              <option value="start_date">{t.startDate}</option>
              <option value="created_date">{t.createdDate}</option>
              <option value="standard">
                {t.chapterSort}
              </option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
              <svg
                className="h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {t.tableHeaders.map((header) => (
                <th
                  key={header}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                  {t.loadingAssignments}
                </td>
              </tr>
            ) : assignments.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                  {searchQuery.trim()
                    ? `No assignments found matching "${searchQuery}"`
                    : "No assignments found"}
                </td>
              </tr>
            ) : (
              assignments.map((a, i) => {
                const completion = completionMap[a.id] || {
                  total: 0,
                  completed: 0,
                };
                const percent =
                  completion.total > 0
                    ? Math.round(
                        (completion.completed / completion.total) * 100
                      )
                    : 0;
                return (
                  <tr key={i}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {a.assessment_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {a?.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          typeColors[a.type] || "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {a.type === "pe_chapter"
                          ? t.typeChapter
                          : a.type.charAt(0).toUpperCase() + a.type.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{a.standard}</div>
                      <div className="text-xs text-gray-500">{a.chapter}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {a.assigned_class
                          ? t.allStudents
                          : t.selectStudents(a.selected_students?.length || 0)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {a.assign_to === "individuals" && (
                          <span>{t.individual}</span>
                        )}
                        {a.assign_to === "class" &&
                          a.assigned_class?.map((c) => c.name).join(", ")}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-green-600 font-medium">
                        {fmtDate(a.start_date)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {fmtTime(a.start_date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-red-600 font-medium">
                        {fmtDate(a.due_date)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {fmtTime(a.due_date)}
                      </div>
                      <div className="text-xs text-gray-500">
                        <span>
                          {(() => {
                            const startDate = dayjs(a.start_date);
                            const now = dayjs();

                            if (startDate.isAfter(now)) {
                              return "Not started";
                            }

                            const dueDate = dayjs(a.due_date);
                            const daysLeft = dueDate.diff(now, "day");
                            return daysLeft > 0
                              ? t.daysLeft(daysLeft)
                              : t.dueToday;
                          })()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() =>
                              handleRetakeChange(
                                a.id,
                                a.retake_option === "oneRetake"
                                  ? "noRetake"
                                  : "oneRetake"
                              )
                            }
                            disabled={updatingRetake}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                              a.retake_option === "oneRetake"
                                ? "bg-indigo-600"
                                : "bg-gray-200"
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                a.retake_option === "oneRetake"
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </button>
                        </div>
                        {updatingRetake && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-green-600 h-2.5 rounded-full"
                          style={{ width: `${percent}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {t.completed(percent, completion.completed, completion.total)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2 justify-end">
                        <Link
                          to={
                            // pe_chapter View opens the teacher lesson PREVIEW
                            // (owner-scoped, role="teacher" → the doc-hub aside +
                            // Assessment tab render), keyed by the assignment ROW
                            // id. NOT the /quiz player (404s "Quiz not found" for
                            // PE) and NOT /student/lesson (student-row-gated, so a
                            // teacher hits MissingChapter). PFL/quiz rows unchanged.
                            a.type === "pe_chapter"
                              ? `/teacher/lesson-preview/${a.id}`
                              : `/${getRouteBase(a.type)}/${a.assessment_id}`
                          }
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          {t.view}
                        </Link>
                        <button
                          onClick={() => handleEditAssignment(a)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {t.edit}
                        </button>
                        <button
                          onClick={() => handleDeleteAssignment(a)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t.delete}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-between items-center">
        <div className="text-sm text-gray-700">
          {assignments.length > 0
            ? t.rangeAssignments(
                (currentPage - 1) * itemsPerPage + 1,
                Math.min(currentPage * itemsPerPage, totalCount),
                totalCount
              )
            : t.noAssignments}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1 || loading}
            className={`px-3 py-1 border border-gray-300 rounded-md text-sm ${
              currentPage === 1 || loading
                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                : "text-gray-700 bg-white hover:bg-gray-50"
            }`}
          >
            {t.previous}
          </button>
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages || loading}
            className={`px-3 py-1 border border-gray-300 rounded-md text-sm ${
              currentPage === totalPages || loading
                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                : "text-gray-700 bg-white hover:bg-gray-50"
            }`}
          >
            {t.next}
          </button>
        </div>
      </div>

      <ConfirmDeleteAssignmentModal
        userId={user?.id}
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setAssignmentToDelete(null);
        }}
        assignment={assignmentToDelete}
        onAssignmentDeleted={handleAssignmentDeleted}
      />

      <EditAssignmentModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setAssignmentToEdit(null);
        }}
        assignment={assignmentToEdit}
        onAssignmentUpdated={handleAssignmentUpdated}
      />
    </div>
  );
}
